import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  useTheme,
  Tab,
  Tabs,
  IconButton,
  Card,
  CardContent
} from '@mui/material';
import { MatchData } from '@/interfaces/liveGamesType';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, Legend } from 'recharts';
import { FiInfo, FiRefreshCw, FiMaximize, FiTrendingUp } from 'react-icons/fi';
import { getMatchPredictions } from '../../services/api';
import { motion } from 'framer-motion';

interface MatchPredictionsProps {
  match: MatchData;
  isLoading?: boolean;
}

interface Prediction {
  match_id: string;
  match_hometeam_id: string;
  match_hometeam_name: string;
  match_awayteam_name: string;
  match_awayteam_id: string;
  prob_HW: string; // Probabilidade de vitória do time da casa
  prob_D: string;  // Probabilidade de empate
  prob_AW: string; // Probabilidade de vitória do time visitante
  prob_HW_D: string; // Probabilidade de vitória do time da casa ou empate
  prob_AW_D: string; // Probabilidade de vitória do time visitante ou empate
  prob_bts: string; // Probabilidade de ambos os times marcarem
  prob_O: string;   // Probabilidade de mais de 2.5 gols
  prob_U: string;   // Probabilidade de menos de 2.5 gols
  prob_O1: string;  // Probabilidade de mais de 1.5 gols
  prob_U1: string;  // Probabilidade de menos de 1.5 gols
  prob_O3: string;  // Probabilidade de mais de 3.5 gols
  prob_U3: string;  // Probabilidade de menos de 3.5 gols
}

// Componente principal
const MatchPredictions: React.FC<MatchPredictionsProps> = ({ match, isLoading: propIsLoading = false }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const { isAuthenticated } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);

  // Buscar predições da partida
  const {
    data: predictions,
    isLoading: predictionsLoading,
    refetch
  } = useQuery({
    queryKey: ['matchPredictions', match?.match_id],
    queryFn: () => getMatchPredictions(match.match_id, isAuthenticated),
    enabled: !!match?.match_id,
    staleTime: 3600000, // 1 hora
  });

  // Dados para o gráfico de pizza de resultado
  const resultData = useMemo(() => {
    if (!predictions || !predictions[0]) return [];

    const prediction = predictions[0];
    return [
      { name: 'Vitória Casa', value: parseInt(prediction.prob_HW), color: theme.palette.primary.main },
      { name: 'Empate', value: parseInt(prediction.prob_D), color: theme.palette.grey[500] },
      { name: 'Vitória Fora', value: parseInt(prediction.prob_AW), color: theme.palette.error.main }
    ];
  }, [predictions, theme]);

  // Dados para o gráfico de barras de tendências
  const trendsData = useMemo(() => {
    if (!predictions || !predictions[0]) return [];

    const prediction = predictions[0];
    return [
      { name: 'Over 2.5', value: parseInt(prediction.prob_O), color: parseInt(prediction.prob_O) > 50 ? theme.palette.success.main : theme.palette.error.main },
      { name: 'Ambas Marcam', value: parseInt(prediction.prob_bts), color: parseInt(prediction.prob_bts) > 50 ? theme.palette.success.main : theme.palette.error.main },
      { name: 'Over 3.5', value: parseInt(prediction.prob_O3), color: parseInt(prediction.prob_O3) > 50 ? theme.palette.success.main : theme.palette.error.main },
      { name: 'Under 1.5', value: parseInt(prediction.prob_U1), color: parseInt(prediction.prob_U1) > 50 ? theme.palette.success.main : theme.palette.error.main }
    ];
  }, [predictions, theme]);

  // Verificar se está carregando
  const isLoading = propIsLoading || predictionsLoading || !predictions;

  // Calcular placar mais provável baseado nas probabilidades
  const calculateLikelyScore = () => {
    if (!predictions || !predictions[0]) return { homeScore: 0, awayScore: 0 };

    const prediction = predictions[0];
    const homeWinProb = parseInt(prediction.prob_HW);
    const drawProb = parseInt(prediction.prob_D);
    const awayWinProb = parseInt(prediction.prob_AW);
    const bothTeamsScore = parseInt(prediction.prob_bts);
    const overProb = parseInt(prediction.prob_O);

    let homeScore = 0;
    let awayScore = 0;

    // Determinar quem provavelmente vence
    const maxProb = Math.max(homeWinProb, drawProb, awayWinProb);

    if (maxProb === homeWinProb) {
      // Casa vence
      homeScore = overProb > 60 ? 2 : 1;
      awayScore = bothTeamsScore > 60 ? 1 : 0;
    } else if (maxProb === awayWinProb) {
      // Fora vence
      awayScore = overProb > 60 ? 2 : 1;
      homeScore = bothTeamsScore > 60 ? 1 : 0;
    } else {
      // Empate
      homeScore = bothTeamsScore > 60 ? 1 : 0;
      awayScore = homeScore;
    }

    return { homeScore, awayScore };
  };

  const likelyScore = calculateLikelyScore();
  const prediction = predictions && predictions[0];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFullscreen = () => {
    setFullscreen(!fullscreen);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 300
        }}
      >
        <CircularProgress size={30} />
      </Box>
    );
  }

  // Renderizar o gráfico de pizza de resultado
  const renderResultPieChart = () => (
    <Box sx={{ height: 300, mt: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={resultData}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            label={({ name, value }) => `${name}: ${value}%`}
            labelLine={false}
            animationDuration={1000}
            animationBegin={0}
          >
            {resultData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} stroke={isDarkMode ? '#333' : '#fff'} strokeWidth={2} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [`${value}%`, 'Probabilidade']}
            contentStyle={{
              backgroundColor: isDarkMode ? '#333' : '#fff',
              border: `1px solid ${theme.palette.divider}`
            }}
          />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );

  // Renderizar o gráfico de barras de tendências
  const renderTrendBars = () => (
    <Box sx={{ height: 300, mt: 2 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={trendsData}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <XAxis dataKey="name" />
          <Tooltip
            formatter={(value) => [`${value}%`, 'Probabilidade']}
            contentStyle={{
              backgroundColor: isDarkMode ? '#333' : '#fff',
              border: `1px solid ${theme.palette.divider}`
            }}
          />
          <Bar dataKey="value" radius={[10, 10, 0, 0]}>
            {trendsData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  // Renderizar o placar mais provável
  const renderLikelyScore = () => (
    <Box sx={{ height: 300, mt: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          elevation={8}
          sx={{
            width: 300,
            borderRadius: 4,
            overflow: 'hidden',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          }}
        >
          <Box sx={{ p: 2, textAlign: 'center', color: 'white' }}>
            <Typography variant="h6" fontWeight="bold">Placar Mais Provável</Typography>
          </Box>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ textAlign: 'center', width: '40%' }}>
                <Typography variant="body2" color="white" noWrap>
                  {match.match_hometeam_name}
                </Typography>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    color: theme.palette.primary.main,
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    m: 'auto',
                    mt: 1,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}>
                    {likelyScore.homeScore}
                  </Box>
                </motion.div>
              </Box>

              <Typography variant="h5" color="white" sx={{ fontWeight: 'bold' }}>vs</Typography>

              <Box sx={{ textAlign: 'center', width: '40%' }}>
                <Typography variant="body2" color="white" noWrap>
                  {match.match_awayteam_name}
                </Typography>
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                    color: theme.palette.error.main,
                    fontWeight: 'bold',
                    fontSize: '2rem',
                    m: 'auto',
                    mt: 1,
                    boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                  }}>
                    {likelyScore.awayScore}
                  </Box>
                </motion.div>
              </Box>
            </Box>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="white">
                Baseado em análise estatística avançada
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          height: fullscreen ? '80vh' : 'auto',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <FiInfo style={{ marginRight: 8 }} /> Predições Avançadas
          </Typography>
          <Box>
            <IconButton onClick={() => refetch()} size="small" title="Atualizar dados">
              <FiRefreshCw />
            </IconButton>
            <IconButton onClick={handleFullscreen} size="small" title="Tela cheia">
              <FiMaximize />
            </IconButton>
          </Box>
        </Box>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab label="Resultado" />
          <Tab label="Tendências" />
          <Tab label="Placar Provável" />
        </Tabs>

        <Box sx={{ height: fullscreen ? 'calc(80vh - 150px)' : 'auto', position: 'relative' }}>
          {tabValue === 0 && renderResultPieChart()}
          {tabValue === 1 && renderTrendBars()}
          {tabValue === 2 && renderLikelyScore()}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Vitória {match.match_hometeam_name}</Typography>
              <Chip
                label={`${prediction?.prob_HW}%`}
                size="medium"
                color="primary"
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', my: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Empate</Typography>
              <Chip
                label={`${prediction?.prob_D}%`}
                size="medium"
                color="default"
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', my: 1 }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">Vitória {match.match_awayteam_name}</Typography>
              <Chip
                label={`${prediction?.prob_AW}%`}
                size="medium"
                color="error"
                sx={{ fontWeight: 'bold', fontSize: '1.1rem', my: 1 }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="body2" align="center" color="textSecondary">
            Predições baseadas em análise avançada de dados e machine learning
          </Typography>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default MatchPredictions;